import type { NitroFetchOptions } from "nitropack";
import type {
  GlobalFetchOptions,
  SpiritMetaDataItemDto,
  SpiritMetaDataContainerDto,
} from "../types";
import type { UseApiFetchResponse } from "../composables/useApiFetch";
import type {
  MetaDataObjectId,
  SpiritMetaDataLoadContainersDto,
  SpiritMetaDataLoadDto,
  SpiritMetaDataSaveDto,
} from "../types/spirit";

export function $spiritMetaDataResource () {
  const dependencies_ = useFetchDependencies();

  function getByKey (
    objectId: MetaDataObjectId,
    key: string,
    config?: SpiritMetaDataLoadDto,
  ) {
    return get( objectId, "key", key, config );
  }

  function getById (
    objectId: MetaDataObjectId,
    id: number,
    config?: SpiritMetaDataLoadDto,
  ) {
    return get( objectId, "id", id, config );
  }

  function getByAlias (
    objectId: MetaDataObjectId,
    alias: string,
    config?: SpiritMetaDataLoadDto,
  ) {
    return get( objectId, "alias", alias, config );
  }

  function getContainersByKey (
    objectId: MetaDataObjectId,
    key: string,
    config?: SpiritMetaDataLoadDto,
  ) {
    return getContainers( objectId, "key", key, config );
  }

  function getContainersById (
    objectId: MetaDataObjectId,
    id: number,
    config?: SpiritMetaDataLoadDto,
  ) {
    return getContainers( objectId, "id", id, config );
  }

  function getContainersByAlias (
    objectId: MetaDataObjectId,
    alias: string,
    config?: SpiritMetaDataLoadDto,
  ) {
    return getContainers( objectId, "alias", alias, config );
  }

  function get (
    objectId: MetaDataObjectId,
    idKey: "key" | "alias" | "id",
    id: string | number,
    config?: SpiritMetaDataLoadDto,
  ) {
    let url_ = `/api/spirit/metadata/{objectId}/${idKey}/{id}`;

    if ( objectId === undefined || objectId === null ) {
      throw new Error( "The parameter 'objectId' must be defined." );
    }
    url_ = url_.replace( "{objectId}", encodeURIComponent( objectId ) );

    if ( id === undefined || id === null ) {
      throw new Error( `The parameter '${idKey}' must be defined.` );
    }
    url_ = url_.replace( "{id}", encodeURIComponent( id ) );

    if ( config && Object.keys( config ).length > 0 ) {
      let options = "?";

      Object.entries( config ).forEach( ( [paramKey, paramValue] ) => {
        if ( paramValue === undefined || paramValue === null ) {
          return;
        }

        if ( Array.isArray( paramValue ) ) {
          paramValue.forEach( ( val ) => {
            options += `${encodeURIComponent( paramKey )}=${encodeURIComponent( val )}&`;
          } );
        } else {
          options += `${encodeURIComponent( paramKey )}=${encodeURIComponent( paramValue )}&`;
        }
      } );

      options = options.replace( /[?&]$/, "" );

      if ( options.length > 1 ) {
        url_ += options;
      }
    }

    const options_: NitroFetchOptions<string> = {
      method: "GET",
    };

    return $apiFetch<SpiritMetaDataItemDto[]>( url_, options_, dependencies_ );
  }

  function getContainers (
    objectId: MetaDataObjectId,
    idKey: "key" | "alias" | "id",
    id: string | number,
    config?: SpiritMetaDataLoadContainersDto,
  ) {
    let url_ = `/api/spirit/metadata/{objectId}/${idKey}/{id}/containers`;

    if ( objectId === undefined || objectId === null ) {
      throw new Error( "The parameter 'objectId' must be defined." );
    }
    url_ = url_.replace( "{objectId}", encodeURIComponent( objectId ) );

    if ( id === undefined || id === null ) {
      throw new Error( `The parameter '${idKey}' must be defined.` );
    }
    url_ = url_.replace( "{id}", encodeURIComponent( id ) );

    if ( config && Object.keys( config ).length > 0 ) {
      let options = "?";

      Object.entries( config ).forEach( ( [paramKey, paramValue] ) => {
        if ( paramValue === undefined || paramValue === null ) {
          return;
        }

        if ( Array.isArray( paramValue ) ) {
          paramValue.forEach( ( val ) => {
            options += `${encodeURIComponent( paramKey )}=${encodeURIComponent( val )}&`;
          } );
        } else {
          options += `${encodeURIComponent( paramKey )}=${encodeURIComponent( paramValue )}&`;
        }
      } );

      options = options.replace( /[?&]$/, "" );

      if ( options.length > 1 ) {
        url_ += options;
      }
    }

    const options_: NitroFetchOptions<string> = {
      method: "GET",
    };

    return $apiFetch<SpiritMetaDataContainerDto[]>( url_, options_, dependencies_ );
  }

  function save ( value: SpiritMetaDataSaveDto ) {
    const url_ = "/api/spirit/metadata";

    const options_: NitroFetchOptions<string> = {
      method: "POST",
      body: value,
    };

    return $apiFetch<string>( url_, options_, dependencies_ );
  }

  return {
    getByKey,
    getById,
    getByAlias,
    getContainersByKey,
    getContainersById,
    getContainersByAlias,
    save,
  };
}

export function useSpiritMetaDataResource ( options?: GlobalFetchOptions ) {
  function getByKey (
    objectId: MaybeRef<MetaDataObjectId>,
    key: MaybeRef<string>,
    config?: SpiritMetaDataLoadDto,
  ): UseApiFetchResponse<SpiritMetaDataItemDto[]> {
    return get( objectId, "key", key, config );
  }

  function getById (
    objectId: MaybeRef<MetaDataObjectId>,
    id: MaybeRef<number>,
    config?: SpiritMetaDataLoadDto,
  ): UseApiFetchResponse<SpiritMetaDataItemDto[]> {
    return get( objectId, "id", id, config );
  }

  function getByAlias (
    objectId: MaybeRef<MetaDataObjectId>,
    alias: MaybeRef<string>,
    config?: SpiritMetaDataLoadDto,
  ): UseApiFetchResponse<SpiritMetaDataItemDto[]> {
    return get( objectId, "alias", alias, config );
  }

  function getContainersByKey (
    objectId: MaybeRef<MetaDataObjectId>,
    key: MaybeRef<string>,
    config?: SpiritMetaDataLoadDto,
  ): UseApiFetchResponse<SpiritMetaDataContainerDto[]> {
    return getContainers( objectId, "key", key, config );
  }

  function getContainersById (
    objectId: MaybeRef<MetaDataObjectId>,
    id: MaybeRef<number>,
    config?: SpiritMetaDataLoadDto,
  ): UseApiFetchResponse<SpiritMetaDataContainerDto[]> {
    return getContainers( objectId, "id", id, config );
  }

  function getContainersByAlias (
    objectId: MaybeRef<MetaDataObjectId>,
    alias: MaybeRef<string>,
    config?: SpiritMetaDataLoadDto,
  ): UseApiFetchResponse<SpiritMetaDataContainerDto[]> {
    return getContainers( objectId, "alias", alias, config );
  }

  function get (
    objectId: MaybeRef<MetaDataObjectId>,
    idKey: "key" | "alias" | "id",
    id: MaybeRef<string | number>,
    config?: SpiritMetaDataLoadDto,
  ): UseApiFetchResponse<SpiritMetaDataItemDto[]> {
    const computedUrl = computed( () => {
      let url_ = `/api/spirit/metadata/{objectId}/${idKey}/{id}`;

      const objectIdUnref = unref( objectId );
      if ( objectIdUnref === undefined || objectIdUnref === null ) {
        throw new Error( "The parameter 'objectId' must be defined." );
      }
      url_ = url_.replace( "{objectId}", encodeURIComponent( objectIdUnref ) );

      const idUnref = unref( id );
      if ( idUnref === undefined || idUnref === null ) {
        throw new Error( `The parameter '${idKey}' must be defined.` );
      }
      url_ = url_.replace( "{id}", encodeURIComponent( idUnref ) );

      if ( config && Object.keys( config ).length > 0 ) {
        let options = "?";

        Object.entries( config ).forEach( ( [paramKey, paramValue] ) => {
          if ( paramValue === undefined || paramValue === null ) {
            return;
          }

          if ( Array.isArray( paramValue ) ) {
            paramValue.forEach( ( val ) => {
              options += `${encodeURIComponent( paramKey )}=${encodeURIComponent( val )}&`;
            } );
          } else {
            options += `${encodeURIComponent( paramKey )}=${encodeURIComponent( paramValue )}&`;
          }
        } );

        options = options.replace( /[?&]$/, "" );

        if ( options.length > 1 ) {
          url_ += options;
        }
      }

      return url_;
    } );

    const options_: UseApiFetchOptions<SpiritMetaDataItemDto[]> = {
      method: "GET",
      watch: [computedUrl],
      ...options,
    };

    return useApiFetch<SpiritMetaDataItemDto[]>( computedUrl, options_ );
  }

  function getContainers (
    objectId: MaybeRef<MetaDataObjectId>,
    idKey: "key" | "alias" | "id",
    id: MaybeRef<string | number>,
    config?: SpiritMetaDataLoadContainersDto,
  ): UseApiFetchResponse<SpiritMetaDataContainerDto[]> {
    const computedUrl = computed( () => {
      let url_ = `/api/spirit/metadata/{objectId}/${idKey}/{id}/containers`;

      const objectIdUnref = unref( objectId );
      if ( objectIdUnref === undefined || objectIdUnref === null ) {
        throw new Error( "The parameter 'objectId' must be defined." );
      }
      url_ = url_.replace( "{objectId}", encodeURIComponent( objectIdUnref ) );

      const idUnref = unref( id );
      if ( idUnref === undefined || idUnref === null ) {
        throw new Error( `The parameter '${idKey}' must be defined.` );
      }
      url_ = url_.replace( "{id}", encodeURIComponent( idUnref ) );

      if ( config && Object.keys( config ).length > 0 ) {
        let options = "?";

        Object.entries( config ).forEach( ( [paramKey, paramValue] ) => {
          if ( paramValue === undefined || paramValue === null ) {
            return;
          }

          if ( Array.isArray( paramValue ) ) {
            paramValue.forEach( ( val ) => {
              options += `${encodeURIComponent( paramKey )}=${encodeURIComponent( val )}&`;
            } );
          } else {
            options += `${encodeURIComponent( paramKey )}=${encodeURIComponent( paramValue )}&`;
          }
        } );

        options = options.replace( /[?&]$/, "" );

        if ( options.length > 1 ) {
          url_ += options;
        }
      }

      return url_;
    } );

    const options_: UseApiFetchOptions<SpiritMetaDataContainerDto[]> = {
      method: "GET",
      watch: [computedUrl],
      ...options,
    };

    return useApiFetch<SpiritMetaDataContainerDto[]>( computedUrl, options_ );
  }

  return {
    getByKey,
    getById,
    getByAlias,
    getContainersByKey,
    getContainersById,
    getContainersByAlias,
  };
}
